import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const LaPara: React.FC = () => {
    return (
        <NewLayout
            title="La Para makes customers and employees happy with Gallabox | Case Study"
            description="Discover how La Para streamlined bookings, improved guest engagement, and boosted service efficiency with Gallabox’s WhatsApp automation."
            canonical="https://gallabox.com/case-study/la-para"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/laPara/laPara-og.png" }}
        >
            <CaseStudyHerosection
                title="How La Para Transformed Customer Experience with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/laPara/laPara-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "100%", label: "increase in business growth" },
                    { percentage: "90%", label: "boost in team productivity" },
                    { percentage: "80%", label: "reduction in marketing spend" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Hospitality"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "WhatsApp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "WhatsApp Flows" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About La Para">
                            <p className="mt-2">
                                La Para is a fast-growing hospitality and lifestyle brand that delivers premium experiences to its customers. As orders and inquiries increased,
                                their WhatsApp inbox became overwhelming, requiring a shift from manual workflows to automated communication.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <p className="mt-1  ">The company’s growth exposed three main hurdles:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Overwhelmed WhatsApp inbox",
                                        nestedListItem: ["Manual replies and delayed updates left customers waiting, leading to poor experiences."],
                                    },
                                    {
                                        listItem: "Time-consuming workflows",
                                        nestedListItem: [
                                            "Team members spent hours daily on confirmations, shipping updates, and responding to queries, especially during peak seasons.",
                                        ],
                                    },
                                    {
                                        listItem: "Inconsistent communication",
                                        nestedListItem: ["Without automation, customer engagement lacked structure and often caused confusion."],
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Initial solutions tried"} className="mt-12" />
                            <p className="mt-2">La Para relied on:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Manual one-on-one messaging, which quickly became unsustainable",
                                    },
                                    {
                                        listItem: "Trial of other WhatsApp platforms, but they were either too complex, lacked automation, or felt impersonal",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why La Para chose Gallabox"} className="mt-0" />
                            <p className="mt-2">La Para selected Gallabox for its:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Seamless WhatsApp API integration, ensuring reliable workflows.",
                                    },
                                    {
                                        listItem: "Automation flows that delivered efficiency without losing the human touch.",
                                    },
                                    {
                                        listItem: "Balance of branding and personalization, keeping communication warm and consistent with their brand identity.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                            <CaseStudyInformation subTitle={"Goals with Gallabox"} className="mt-10" />
                            <p className="mt-2">La Para set out to: </p>
                            <FeatureList
                                List={[
                                    { listItem: "Improve team productivity" },
                                    { listItem: "Automate customer support" },
                                    { listItem: "Reduce time spent on repetitive queries" },
                                    { listItem: "Deliver consistent, branded experiences at scale" },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on their business growth"} className="mt-0" />
                            <p className="mt-2">The results were exceptional: </p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "100% positive growth impact, proving Gallabox’s role in scaling operations.",
                                    },
                                    {
                                        listItem: "90% boost in employee productivity, freeing teams from daily repetitive tasks.",
                                    },
                                    {
                                        listItem: "90% growth in customer satisfaction, with faster, clearer communication.",
                                    },
                                    {
                                        listItem: "70% increase in customer base, as trust and engagement grew.",
                                    },
                                    {
                                        listItem: "80% reduction in marketing spend, with automation delivering better ROI.",
                                    },
                                    {
                                        listItem: "50% increase in profits, thanks to higher efficiency and reduced costs.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation subTitle={"Turning conversations into customer loyalty"} className="mt-10" />
                            <p className="mt-2">
                                Gallabox has helped La Para reimagine its customer experience. By automating communication and empowering teams, La Para not only saved time and
                                costs but also built stronger connections with its growing base of loyal customers.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default LaPara;
